import { useEffect, useState } from 'react';

import type { AnnotatedCommentMediaKind } from '../model/annotation';

/**
 * Seconds value used when persisting video annotations (`capturedTimestamp ?? getCurrentTime()`).
 * When `capturedTimestamp` is set (e.g. edit mode), stays fixed. Otherwise follows the mounted
 * video element via timeupdate / seeked.
 */
export function useVideoComposerAnnotationSeconds(opts: {
  assetKind: AnnotatedCommentMediaKind;
  capturedTimestamp: number | null;
  getCurrentTime: () => number | null;
  mediaElement: HTMLVideoElement | null;
}): number | null {
  const { assetKind, capturedTimestamp, getCurrentTime, mediaElement } = opts;
  const [liveSeconds, setLiveSeconds] = useState<number | null>(null);

  useEffect(() => {
    if (assetKind !== 'video') return;

    if (capturedTimestamp !== null) {
      setLiveSeconds(null);
      return;
    }

    const sync = () => {
      const t = getCurrentTime();
      setLiveSeconds(t != null && Number.isFinite(t) ? Math.max(0, t) : null);
    };

    sync();

    const el = mediaElement;
    if (!el) return undefined;

    el.addEventListener('timeupdate', sync);
    el.addEventListener('seeked', sync);
    el.addEventListener('loadedmetadata', sync);

    return () => {
      el.removeEventListener('timeupdate', sync);
      el.removeEventListener('seeked', sync);
      el.removeEventListener('loadedmetadata', sync);
    };
  }, [assetKind, capturedTimestamp, mediaElement, getCurrentTime]);

  if (assetKind !== 'video') return null;

  const pinned =
    capturedTimestamp !== null && Number.isFinite(capturedTimestamp) && capturedTimestamp >= 0
      ? capturedTimestamp
      : null;
  return pinned ?? liveSeconds;
}
